import React, { useEffect, useState, useRef, useMemo } from "react";
import GenerateExcel from "../../../../components/GenerateExcel";
import { useDispatch, useSelector } from "react-redux";
import Moment from "react-moment";
import _ from "lodash";
import { getAgenciesPartners, loadAgencies } from "../../../store/partner";
import geoService from "../../../../services/geoService";
import { loadMonthlyInvoiceAgencyCandidates } from "../../../store/reports";
import { formatAmount } from "../../../../services/appService";
import GeneratePdf from "../../../../components/GeneratePdf";
import moment from "moment";
import SearchableSelect from "../../../../comman/SearchableSelect";

const AgencyInvoicePayment = () => {

    const [usStates, setUsStates] = useState([]);
    const [allAgencies, setAllAgencies] = useState([]);
    const [stateAgencies, setSateAgencies] = useState([]);
    const [selectedAgency, setSelectedAgency] = useState(null);
    const [selectedState, setSelectedState] = useState("");
    const [selectedStateAgency, setSelectedStateAgency] = useState("");
    const [allAgenciesCandidates, setAllAgenciesCandidates] = useState([]);
    const [rawInvoices, setRawInvoices] = useState([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [appliedDateRange, setAppliedDateRange] = useState({ start: "", end: "" });
    const [isLoading, setIsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);

    // Live search and scroll states
    const [tableSearchTerm, setTableSearchTerm] = useState("");
    const [tableWidth, setTableWidth] = useState(0);
    const topScrollRef = useRef(null);
    const tableWrapperRef = useRef(null);

    // PDF states
    const [pdfShowHide, setPdfShowHide] = useState(false);
    const [excelShowHide, setExcelShowHide] = useState(false);
    const [selectedCol, setSelectedCol] = useState([]);
    const [selectedColData, setSelectedColData] = useState([]);

    const agenciesPartnersSelector = useSelector(getAgenciesPartners);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(loadAgencies());
        loadUsStates();
    }, []);

    const loadUsStates = async () => {
        const states = await geoService.getStateList();
        setUsStates(states);
    };

    useEffect(() => {
        if (agenciesPartnersSelector.length > 0) {
            setAllAgenciesCandidates(agenciesPartnersSelector);
            const list = _.unionBy(agenciesPartnersSelector, "title");
            setAllAgencies(list);
        }
    }, [agenciesPartnersSelector]);

    const handleAgencyStateChange = (val) => {
        const stateVal = typeof val === "object" && val !== null ? (val.currentTarget ? val.currentTarget.value : (val.value !== undefined ? val.value : "")) : (val || "");
        setSelectedState(stateVal);
        const agencies = allAgencies.filter(
            (x) => x.state === stateVal
        );
        setSateAgencies(agencies);

        // Reset lower dependencies
        setSelectedStateAgency("");
        setSelectedAgency(null);
        setRawInvoices([]);
        setHasFetched(false);
        setTableSearchTerm("");
        setStartDate("");
        setEndDate("");
        setAppliedDateRange({ start: "", end: "" });
    };

    const handleAgencyChange = (val) => {
        const agencyTitle = typeof val === "object" && val !== null ? (val.currentTarget ? val.currentTarget.value : (val.value !== undefined ? val.value : "")) : (val || "");
        const selectedAgencyObj = stateAgencies.find(
            (x) => x.title === agencyTitle
        );

        setSelectedStateAgency(agencyTitle);
        setSelectedAgency(selectedAgencyObj || null);

        // Reset lower dependencies
        setRawInvoices([]);
        setHasFetched(false);
        setTableSearchTerm("");
        setStartDate("");
        setEndDate("");
        setAppliedDateRange({ start: "", end: "" });
    };

    const handleGenerateRpt = async () => {
        if (!selectedStateAgency || isLoading) return;

        setIsLoading(true);
        setHasFetched(true);
        setRawInvoices([]);
        setTableSearchTerm("");

        let agencyCandidates = [];
        if (selectedStateAgency === "all") {
            stateAgencies.forEach((item) => {
                const cand = allAgenciesCandidates.filter((x) => x.title === item.title);
                agencyCandidates = [...agencyCandidates, ...cand];
            });
        } else {
            agencyCandidates = allAgenciesCandidates.filter(
                (x) => x.title === selectedStateAgency
            );
        }

        const uniqueCandidates = _.uniqBy(agencyCandidates, "performerid");

        try {
            const results = [];
            const chunkSize = 5;
            for (let i = 0; i < uniqueCandidates.length; i += chunkSize) {
                const chunk = uniqueCandidates.slice(i, i + chunkSize);
                const chunkPromises = chunk.map(item =>
                    dispatch(loadMonthlyInvoiceAgencyCandidates(item.performerid))
                );
                const chunkResults = await Promise.all(chunkPromises);
                results.push(...chunkResults);
            }

            const allInvoices = results.filter(Boolean).flat();
            const uniqueInvoices = _.uniqBy(allInvoices, "invoiceNo");

            setRawInvoices(uniqueInvoices);
        } catch (error) {
            console.error("Failed to load invoice payments:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Declarative filtering
    const filteredReports = useMemo(() => {
        if (!rawInvoices) return [];
        return rawInvoices.filter(item => {
            // 1. Search term match
            const term = tableSearchTerm.toLowerCase();
            const candName = `${item.candidateName || ""} ${item.candidateLastName || ""}`.toLowerCase();
            const invNo = (item.invoiceNo || "").toLowerCase();
            const candId = String(item.candidateId || "").toLowerCase();

            const matchesSearch = term === "" ||
                candName.includes(term) ||
                invNo.includes(term) ||
                candId.includes(term);

            if (!matchesSearch) return false;

            // 2. Date range match
            if (appliedDateRange.start || appliedDateRange.end) {
                if (!item.invoiceDate) return false;
                const invDateStr = new Date(item.invoiceDate).toISOString().split("T")[0];
                if (appliedDateRange.start && invDateStr < appliedDateRange.start) {
                    return false;
                }
                if (appliedDateRange.end && invDateStr > appliedDateRange.end) {
                    return false;
                }
            }

            return true;
        });
    }, [rawInvoices, tableSearchTerm, appliedDateRange]);

    const taxRate = selectedAgency ? parseFloat(selectedAgency.clientId) || 0 : 0;

    // KPI stats computations
    const totalCandidatesCount = useMemo(() => {
        const uniqueCands = _.uniqBy(filteredReports, "candidateId");
        return uniqueCands.length;
    }, [filteredReports]);

    const grossInvoiced = useMemo(() => {
        return filteredReports.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0);
    }, [filteredReports]);

    const totalTax = useMemo(() => {
        return filteredReports.reduce((sum, item) => {
            const amt = parseFloat(item.totalAmount) || 0;
            return sum + (amt * taxRate) / 100;
        }, 0);
    }, [filteredReports, taxRate]);

    const netClientValue = useMemo(() => {
        return grossInvoiced - totalTax;
    }, [grossInvoiced, totalTax]);

    const getAgencyMonPayTotal = () => {
        return (
            <tr className="ach-pay-total-row">
                <td colSpan={5} className="font-weight-bold text-right ach-pay-total-label">Total</td>
                <td className="font-weight-bold text-right ach-pay-total-value">
                    {"$" + formatAmount(grossInvoiced)}
                </td>
                <td className="font-weight-bold text-right ach-pay-total-value">
                    {"$" + formatAmount(totalTax)}
                </td>
            </tr>
        );
    };

    const generateAgencyReport = () => {
        const headers = [
            [
                "#",
                "Name of Candidate",
                "IPCS Invoice #",
                "Date Invoiced",
                "Date Paid",
                "Amount",
                `Tax (${taxRate}%)`
            ],
        ];

        const colData = [];

        filteredReports.forEach((item) => {
            const amt = parseFloat(item.totalAmount) || 0;
            const tax = (amt * taxRate) / 100;

            colData.push([
                item.candidateId || "",
                `${item.candidateName || ""} ${item.candidateLastName || ""}`,
                item.invoiceNo || "",
                item.invoiceDate ? moment(item.invoiceDate).format("MMM Do, YYYY") : "",
                item.paymentDate ? moment(item.paymentDate).format("MMM Do, YYYY") : "",
                "$" + formatAmount(amt),
                "$" + formatAmount(tax)
            ]);
        });

        // Total Row for PDF
        colData.push([
            "Total",
            "",
            "",
            "",
            "",
            "$" + formatAmount(grossInvoiced),
            "$" + formatAmount(totalTax)
        ]);

        setSelectedCol(headers);
        setSelectedColData(colData);
        setPdfShowHide(true);
    }

    const generateAgencyReportExcel = () => {
        const headers = [
            [
                "#",
                "Name of Candidate",
                "IPCS Invoice #",
                "Date Invoiced",
                "Date Paid",
                "Amount",
                `Tax (${taxRate}%)`
            ],
        ];

        const colData = [];

        filteredReports.forEach((item) => {
            const amt = parseFloat(item.totalAmount) || 0;
            const tax = (amt * taxRate) / 100;

            colData.push([
                item.candidateId || "",
                `${item.candidateName || ""} ${item.candidateLastName || ""}`,
                item.invoiceNo || "",
                item.invoiceDate ? moment(item.invoiceDate).format("MMM Do, YYYY") : "",
                item.paymentDate ? moment(item.paymentDate).format("MMM Do, YYYY") : "",
                "$" + formatAmount(amt),
                "$" + formatAmount(tax)
            ]);
        });

        // Total Row for PDF
        colData.push([
            "Total",
            "",
            "",
            "",
            "",
            "$" + formatAmount(grossInvoiced),
            "$" + formatAmount(totalTax)
        ]);

        setSelectedCol(headers);
        setSelectedColData(colData);
        setExcelShowHide(true);
    };

    const handlepdfShowHide = () => {
        setPdfShowHide(false);
        setSelectedCol([]);
        setSelectedColData([]);
    };

    const handleexcelShowHide = () => {
        setExcelShowHide(false);
        setSelectedCol([]);
        setSelectedColData([]);
    };

    // Scroll synchronization
    const handleTopScroll = () => {
        if (topScrollRef.current && tableWrapperRef.current) {
            tableWrapperRef.current.scrollLeft = topScrollRef.current.scrollLeft;
        }
    };

    const handleTableScroll = () => {
        if (topScrollRef.current && tableWrapperRef.current) {
            topScrollRef.current.scrollLeft = tableWrapperRef.current.scrollLeft;
        }
    };

    useEffect(() => {
        const updateWidth = () => {
            const table = document.getElementById("agency_invoice_payment_table");
            if (table) {
                setTableWidth(table.scrollWidth);
            }
        };

        updateWidth();
        const timer = setTimeout(updateWidth, 500);

        window.addEventListener("resize", updateWidth);
        return () => {
            window.removeEventListener("resize", updateWidth);
            clearTimeout(timer);
        };
    }, [filteredReports, selectedStateAgency, rawInvoices]);

    return (
        <div className="ach-pay-container">
            <style>{`
                .ach-pay-container {
                    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                    color: #1e293b;
                    margin-bottom: 2rem;
                }

                /* KPI Metric cards */
                .ach-pay-kpi-row {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
                    gap: 16px;
                }

                .ach-pay-metric-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 0 !important;
                    padding: 14px 18px;
                    display: flex;
                    align-items: center;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.01);
                }

                .ach-pay-metric-icon {
                    width: 38px;
                    height: 38px;
                    border-radius: 0 !important;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                    margin-right: 14px;
                    background: #f5f5f5;
                    color: #171717;
                    border: 1px solid #d4d4d4;
                    flex-shrink: 0;
                }

                .ach-pay-metric-val {
                    font-size: 18px;
                    font-weight: 500;
                    color: #1e293b;
                    line-height: 1.1;
                    margin-bottom: 2px;
                }

                .ach-pay-metric-label {
                    font-size: 12px;
                    color: #64748b;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.02em;
                }

                /* Controls Layout */
                .ach-pay-controls {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    margin-bottom: 18px;
                    background-color: #fafbfc;
                    border: 1px solid #e2e8f0;
                    border-radius: 0 !important;
                    padding: 18px 20px;
                }

                .ach-pay-controls-row-top {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid #e2e8f0;
                    padding-bottom: 14px;
                    width: 100%;
                    gap: 16px;
                    flex-wrap: wrap;
                }

                .ach-pay-controls-row-bottom {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                    width: 100%;
                }

                .ach-pay-btn-generate {
                    margin-left: auto !important;
                }

                .ach-pay-search-wrapper {
                    position: relative;
                    width: 240px;
                }

                .ach-pay-search-input {
                    width: 100%;
                    padding: 8px 12px 8px 34px;
                    background: #ffffff;
                    border: 1px solid #cbd5e1;
                    border-radius: 0 !important;
                    font-size: 14px;
                    font-weight: 400;
                    color: #1e293b;
                    transition: all 0.2s ease;
                
                    height: 35px !important;
                    box-sizing: border-box !important;
                }

                .ach-pay-search-input:focus {
                    outline: none;
                    border-color: #171717;
                    box-shadow: 0 0 0 3px rgba(23, 23, 23, 0.1);
                }

                .ach-pay-search-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    font-size: 12px;
                }

                .ach-pay-filter-select {
                    height: 35px;
                    border: 1px solid #cbd5e1;
                    border-radius: 0 !important;
                    padding: 0 10px;
                    font-size: 14px;
                    font-weight: 400;
                    color: #1e293b;
                    background: #ffffff;
                    max-width: 180px;
                }

                .ach-pay-filter-select:focus {
                    outline: none;
                    border-color: #171717;
                }

                .ach-pay-actions-btns {
                    display: flex;
                    align-items: center;
                }

                .ach-pay-actions-btns > * {
                    margin-right: 8px !important;
                }

                .ach-pay-actions-btns > *:last-child {
                    margin-right: 0 !important;
                }

                .ach-pay-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    padding: 7px 14px;
                    height: 35px !important;
                    min-height: 35px !important;
                    box-sizing: border-box !important;
                    border-radius: 0 !important;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    border: 1px solid #cbd5e1;
                    background: #ffffff;
                    color: #1e293b;
                    box-shadow: 0 2px 5px rgba(15, 23, 42, 0.08) !important;
                }

                .ach-pay-btn:hover:not(:disabled) {
                    background-color: #f5f5f5;
                    color: #1e293b;
                    border-color: #94a3b8;
                }

                .ach-pay-btn:disabled {
                    background-color: #ffffff !important;
                    border-color: #cbd5e1 !important;
                    color: #94a3b8 !important;
                    box-shadow: 0 2px 5px rgba(15, 23, 42, 0.08) !important;
                    cursor: not-allowed;
                }

                .ach-pay-btn-primary {
                    background: #2563eb !important;
                    color: #ffffff !important;
                    border: 1px solid #2563eb !important;
                    box-shadow: 0 2px 5px rgba(37, 99, 235, 0.2) !important;
                    transition: all 0.15s ease;
                    padding: 7px 16px !important;
                    height: 35px !important;
                    min-height: 35px !important;
                    box-sizing: border-box !important;
                }

                .ach-pay-btn-primary:hover:not(:disabled) {
                    background: #1d4ed8 !important;
                    border-color: #1d4ed8 !important;
                    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3) !important;
                }

                .ach-pay-btn-primary:disabled {
                    background-color: #dbe2ea !important;
                    border-color: #cbd5e1 !important;
                    color: #94a3b8 !important;
                    box-shadow: none !important;
                    cursor: not-allowed;
                    opacity: 0.85 !important;
                }

                /* Table modifications matching WorkflowUser */
                .ach-pay-table-wrapper {
                    border: 1px solid #e2e8f0;
                    border-radius: 0 !important;
                    overflow-x: auto;
                    overflow-y: hidden;
                    background: white;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.01);
                }

                .ach-pay-table-wrapper::-webkit-scrollbar,
                .ach-pay-report-container div::-webkit-scrollbar {
                    width: 6px !important;
                    height: 6px !important;
                }

                .ach-pay-table-wrapper::-webkit-scrollbar-track,
                .ach-pay-report-container div::-webkit-scrollbar-track {
                    background: transparent !important;
                }

                .ach-pay-table-wrapper::-webkit-scrollbar-thumb,
                .ach-pay-report-container div::-webkit-scrollbar-thumb {
                    background: linear-gradient(180deg, #94a3b8, #cbd5e1) !important;
                    border-radius: 0 !important;
                    transition: all 0.2s ease !important;
                }

                .ach-pay-table-wrapper::-webkit-scrollbar-thumb:hover,
                .ach-pay-report-container div::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(180deg, #64748b, #94a3b8) !important;
                }

                .ach-pay-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 14px;
                    text-align: left;
                }

                .ach-pay-table thead {
                    background: linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%) !important;
                }

                .ach-pay-table thead tr {
                    background: transparent !important;
                }

                .ach-pay-table th {
                    background: transparent !important;
                    background-color: transparent !important;
                    color: #64748b !important;
                    font-weight: 600 !important;
                    font-size: 12px !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.6px !important;
                    padding: 13px 16px !important;
                    border-bottom: 2px solid #e2e8f0 !important;
                    border-top: none !important;
                    border-left: none !important;
                    border-right: none !important;
                    white-space: nowrap !important;
                }

                .ach-pay-table tbody tr {
                    transition: background 0.15s ease, box-shadow 0.15s ease !important;
                }

                .ach-pay-table tbody tr:hover {
                    background: linear-gradient(90deg, #f5f5f5 0%, #fafafa 100%) !important;
                    box-shadow: inset 3px 0 0 0 #171717 !important;
                }

                .ach-pay-table tbody tr:hover td {
                    color: #1e293b !important;
                }

                .ach-pay-table td {
                    padding: 13px 16px !important;
                    font-size: 14px !important;
                    font-weight: 400 !important;
                    color: #1e293b !important;
                    border-bottom: 1px solid #f1f5f9 !important;
                    vertical-align: middle !important;
                    transition: color 0.15s ease !important;
                    white-space: nowrap !important;
                }

                .ach-pay-table tbody tr:last-child td {
                    border-bottom: none !important;
                }

                .ach-pay-table td.col-amount {
                    color: #1e293b !important;
                    font-weight: 500 !important;
                    background-color: transparent !important;
                    text-align: right !important;
                }

                /* Total Payment row layout */
                .ach-pay-total-row {
                    background-color: #f8fafc !important;
                    border-top: 2px solid #cbd5e1 !important;
                    border-bottom: 2px solid #cbd5e1 !important;
                }

                .ach-pay-total-row td {
                    color: #1e293b !important;
                    font-size: 14px !important;
                    font-weight: 500 !important;
                    padding: 14px 16px !important;
                }

                .ach-pay-total-label {
                    text-transform: uppercase !important;
                    letter-spacing: 0.6px !important;
                    font-size: 12px !important;
                    color: #64748b !important;
                    text-align: right !important;
                    font-weight: 600 !important;
                }

                .ach-pay-total-value {
                    font-size: 14px !important;
                    color: #1e293b !important;
                    font-weight: 500 !important;
                    background-color: #f8fafc !important;
                    text-align: right !important;
                }

                /* Shimmer and Loading Animation */
                @keyframes shimmer {
                  0%   { background-position: -600px 0; }
                  100% { background-position: 600px 0; }
                }

                @keyframes loadingBar {
                  0%   { width: 0%; opacity: 1; }
                  70%  { width: 85%; opacity: 1; }
                  100% { width: 100%; opacity: 0; }
                }

                .wf-loading-bar {
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  height: 3px;
                  background: linear-gradient(90deg, #171717, #737373, #171717);
                  background-size: 200% 100%;
                  animation: loadingBar 1.6s ease-in-out infinite;
                  z-index: 10;
                }

                .skeleton-cell {
                  display: block;
                  height: 12px;
                  border-radius: 0 !important;
                  background: linear-gradient(
                    90deg,
                    #f1f5f9 25%,
                    #e2e8f0 50%,
                    #f1f5f9 75%
                  );
                  background-size: 600px 100%;
                  animation: shimmer 1.4s ease-in-out infinite;
                }

                .skeleton-cell.sk-short  { width: 40%; }
                .skeleton-cell.sk-medium { width: 65%; }
                .skeleton-cell.sk-long   { width: 85%; }

                .skeleton-row td {
                  padding: 14px 16px !important;
                }

                /* Enhanced empty state */
                .wf-empty-state {
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  padding: 50px 24px;
                  text-align: center;
                  gap: 12px;
                }

                .wf-empty-icon {
                  width: 52px;
                  height: 52px;
                  border-radius: 0 !important;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 22px;
                  background: #f5f5f5;
                  border: 1px solid #d4d4d4;
                  color: #171717;
                  margin-bottom: 4px;
                }

                .wf-empty-title {
                  font-size: 14px;
                  font-weight: 500;
                  color: #1e293b;
                  margin: 0;
                  width: 100%;
                  text-align: center;
                }

                .wf-empty-desc {
                  font-size: 14px;
                  font-weight: 400;
                  color: #64748b;
                  margin: 0 auto;
                  line-height: 1.6;
                  text-align: center;
                  width: 100%;
                }
            `}</style>

            <div className="ach-pay-controls">
                {/* Row 1: Search and Excel/PDF Export buttons */}
                <div className="ach-pay-controls-row-top">
                    <div className="ach-pay-search-wrapper">
                        <i className="fa fa-search ach-pay-search-icon"></i>
                        <input
                            type="text"
                            placeholder="Search candidate, invoice..."
                            className="ach-pay-search-input"
                            value={tableSearchTerm}
                            onChange={(e) => setTableSearchTerm(e.target.value)}
                            disabled={rawInvoices.length === 0}
                        />
                    </div>

                    <div className="ach-pay-actions-btns">
                        <button
                            type="button"
                            className="ach-pay-btn"
                            disabled={filteredReports.length === 0}
                            onClick={generateAgencyReportExcel}
                        >
                            <i className="fa fa-file-excel-o" style={{ color: "#1e293b", marginRight: "6px" }} aria-hidden="true"></i>
                            Excel Export
                        </button>
                        <button
                            type="button"
                            className="ach-pay-btn"
                            disabled={filteredReports.length === 0}
                            onClick={generateAgencyReport}
                        >
                            <i className="fa fa-file-pdf-o" style={{ color: "#1e293b", marginRight: "6px" }}></i>
                            Export PDF
                        </button>
                    </div>
                </div>

                {/* Row 2: Filtering and Generation section */}
                <div className="ach-pay-controls-row-bottom">
                    <SearchableSelect
                        id="usState"
                        name="usState"
                        size="sm"
                        width="180px"
                        icon="fa-map-marker"
                        placeholder="Select US State"
                        value={selectedState}
                        onChange={handleAgencyStateChange}
                        isDisabled={isLoading}
                        options={usStates.map((item) => ({
                            value: item.name,
                            label: item.name,
                        }))}
                    />

                    <SearchableSelect
                        id="agency"
                        name="agency"
                        size="sm"
                        width="200px"
                        icon="fa-university"
                        placeholder="Select Agency"
                        isDisabled={stateAgencies.length === 0 || isLoading}
                        value={selectedStateAgency}
                        onChange={handleAgencyChange}
                        options={[
                            { value: "all", label: "All Agencies" },
                            ...stateAgencies.map((item) => ({
                                value: item.title,
                                label: item.title,
                            }))
                        ]}
                    />

                    {/* Date filtering controls */}
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                        <span style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>From:</span>
                        <input
                            type="date"
                            className="ach-pay-filter-select"
                            style={{ height: "35px", padding: "0 8px", width: "135px" }}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            disabled={isLoading}
                        />
                        <span style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>To:</span>
                        <input
                            type="date"
                            className="ach-pay-filter-select"
                            style={{ height: "35px", padding: "0 8px", width: "135px" }}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            disabled={isLoading}
                        />
                        <button
                            type="button"
                            className="ach-pay-btn"
                            style={{ height: "35px" }}
                            onClick={() => setAppliedDateRange({ start: startDate, end: endDate })}
                            disabled={(!startDate && !endDate) || isLoading}
                        >
                            Apply Dates
                        </button>
                        {(appliedDateRange.start || appliedDateRange.end) && (
                            <button
                                type="button"
                                className="ach-pay-btn"
                                style={{ height: "35px", color: "#ef4444" }}
                                onClick={() => {
                                    setStartDate("");
                                    setEndDate("");
                                    setAppliedDateRange({ start: "", end: "" });
                                }}
                                disabled={isLoading}
                            >
                                Clear
                            </button>
                        )}
                    </div>

                    <button
                        type="button"
                        className="ach-pay-btn ach-pay-btn-primary ach-pay-btn-generate"
                        disabled={!selectedStateAgency || isLoading}
                        onClick={handleGenerateRpt}
                    >
                        <i className="fa fa-refresh" style={{ fontSize: "11px", marginRight: "6px" }}></i>
                        Generate Report
                    </button>
                </div>
            </div>

            {selectedState === "" ? (
                <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "0", background: "#f8fafc" }}>
                    <div className="wf-empty-icon">
                        <i className="fa fa-map-marker"></i>
                    </div>
                    <p className="wf-empty-title">No US State Selected</p>
                    <p className="wf-empty-desc">Choose a US state from the controls above to filter client agencies.</p>
                </div>
            ) : selectedStateAgency === "" ? (
                <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "0", background: "#f8fafc" }}>
                    <div className="wf-empty-icon">
                        <i className="fa store-icon fa-building-o"></i>
                    </div>
                    <p className="wf-empty-title">No Agency Selected</p>
                    <p className="wf-empty-desc">Choose a client agency from the controls above to access candidate invoices.</p>
                </div>
            ) : (
                <div className="ach-pay-report-body p-0">
                    {/* KPI Metrics */}
                    {rawInvoices.length > 0 && (
                        <div className="ach-pay-kpi-row" style={{ marginTop: "18px", marginBottom: "18px" }}>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon">
                                    <i className="fa fa-users"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalCandidatesCount}</div>
                                    <div className="ach-pay-metric-label">Active Candidates</div>
                                </div>
                            </div>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon">
                                    <i className="fa fa-usd"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{"$" + formatAmount(grossInvoiced)}</div>
                                    <div className="ach-pay-metric-label">Gross Invoiced</div>
                                </div>
                            </div>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon">
                                    <i className="fa fa-percent"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{"$" + formatAmount(totalTax)}</div>
                                    <div className="ach-pay-metric-label">Total Tax / Fees ({taxRate}%)</div>
                                </div>
                            </div>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon">
                                    <i className="fa fa-briefcase"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{"$" + formatAmount(netClientValue)}</div>
                                    <div className="ach-pay-metric-label">Net Client Value</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Synchronized top scrollbar - only takes space when table overflows */}
                    {rawInvoices.length > 0 && filteredReports.length > 0 && tableWidth > (tableWrapperRef.current?.clientWidth || 0) && (
                        <div
                            ref={topScrollRef}
                            onScroll={handleTopScroll}
                            style={{ overflowX: "auto", overflowY: "hidden", height: "10px", width: "100%", marginBottom: "4px", marginTop: "4px" }}
                        >
                            <div style={{ width: `${tableWidth}px`, height: "1px" }}></div>
                        </div>
                    )}

                    <div
                        ref={tableWrapperRef}
                        onScroll={handleTableScroll}
                        className="ach-pay-table-wrapper"
                        style={{ position: "relative" }}
                    >
                        {isLoading && <div className="wf-loading-bar" />}

                        {isLoading ? (
                            /* SKELETON TABLE LOADING SCREEN */
                            <table className="ach-pay-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Name of Candidate</th>
                                        <th>IPCS Invoice #</th>
                                        <th>Date Invoiced</th>
                                        <th>Date Paid</th>
                                        <th className="text-right">Amount</th>
                                        <th className="text-right">Tax ({taxRate}%)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <tr key={`skel-${i}`} className="skeleton-row">
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.07}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.06}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.08}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.07}s` }} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : rawInvoices.length > 0 ? (
                            filteredReports.length > 0 ? (
                                <table
                                    className="ach-pay-table"
                                    id="agency_invoice_payment_table"
                                >
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Name of Candidate</th>
                                            <th>IPCS Invoice #</th>
                                            <th>Date Invoiced</th>
                                            <th>Date Paid</th>
                                            <th className="text-right">Amount</th>
                                            <th className="text-right">Tax ({taxRate}%)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredReports.map((item) => {
                                            const amt = parseFloat(item.totalAmount) || 0;
                                            const tax = (amt * taxRate) / 100;
                                            return (
                                                <tr key={item.invoiceNo}>
                                                    <td>{item.candidateId}</td>
                                                    <td>{item.candidateName}&nbsp;{item.candidateLastName}</td>
                                                    <td>{item.invoiceNo}</td>
                                                    <td>
                                                        {item.invoiceDate && (
                                                            <Moment format="MMM Do, YYYY">{item.invoiceDate}</Moment>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {item.paymentDate && (
                                                            <Moment format="MMM Do, YYYY">{item.paymentDate}</Moment>
                                                        )}
                                                    </td>
                                                    <td className="col-amount text-right">
                                                        {"$" + formatAmount(amt)}
                                                    </td>
                                                    <td className="col-amount text-right">
                                                        {"$" + formatAmount(tax)}
                                                    </td>
                                                </tr>
                                            );
                                        })}

                                        {getAgencyMonPayTotal()}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="wf-empty-state">
                                    <div className="wf-empty-icon">
                                        <i className="fa fa-search"></i>
                                    </div>
                                    <p className="wf-empty-title">No Matching Records</p>
                                    <p className="wf-empty-desc">No invoice entries matched your search query. Try adjusting your search term.</p>
                                </div>
                            )
                        ) : !hasFetched ? (
                            <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "0", background: "#f8fafc" }}>
                                <div className="wf-empty-icon">
                                    <i className="fa fa-play-circle-o"></i>
                                </div>
                                <p className="wf-empty-title">Ready to Load Data</p>
                                <p className="wf-empty-desc">Click the "Generate Report" button above to fetch candidate invoices for this selection.</p>
                            </div>
                        ) : (
                            <div className="wf-empty-state">
                                <div className="wf-empty-icon">
                                    <i className="fa fa-folder-open-o"></i>
                                </div>
                                <p className="wf-empty-title">No Invoices Found</p>
                                <p className="wf-empty-desc">No invoice records found for this selection.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <GeneratePdf
                handlepdfShowHide={handlepdfShowHide}
                pdfShowHide={pdfShowHide}
                selectedCol={selectedCol}
                selectedColData={selectedColData}
            ></GeneratePdf>
            <GenerateExcel
                handleexcelShowHide={handleexcelShowHide}
                excelShowHide={excelShowHide}
                selectedCol={selectedCol}
                selectedColData={selectedColData}
            ></GenerateExcel>
        </div>
    );
};

export default AgencyInvoicePayment;